import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, FileWarning, Info, Upload } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import { formatarDataHora } from '@/lib/format';
import {
  AvisoDeFalha,
  Chave,
  CLASSE_DE_CAMPO,
  EtiquetaDeVersao,
  ListaDeDiagnosticos,
  Secao,
} from '@/features/workflow/componentes';
import { LinkDaListaDeFluxos } from '@/features/workflow/navegacao';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { lerDefinicao, proximoNumeroDeVersao } from '@/features/workflow/regras';
import type { DefinicaoDeFluxo, Diagnostico, ResumoDeVersao } from '@/features/workflow/tipos';
import {
  chavesDeFluxo,
  listarDefinicoes,
  obterDiagnosticos,
  publicarVersao,
  salvarRascunho,
} from '@/features/workflow/workflow.api';

/**
 * Uma definição: as versões dela, o rascunho e a publicação.
 *
 * ## Editor de texto, e não de grafo
 *
 * O desenho visual do grafo é outra empreitada. O que esta tela faz é o que a
 * API oferece hoje — guardar JSON, validar e publicar — sem esconder que é
 * JSON. Para quem administra o processo isso é pouco: é preciso conhecer o
 * formato da definição para escrever a primeira. Em compensação, nada aqui
 * mente sobre o que o servidor sabe fazer, e o botão "modelo mínimo" dá um
 * ponto de partida válido.
 *
 * ## Por que a validação é a peça central
 *
 * `POST …/publish` responde 422 com **todos** os problemas quando o grafo não
 * fecha. O filtro global do servidor, porém, entrega só a mensagem — a lista
 * estruturada, com o caminho de cada problema, mora em
 * `GET …/versions/:id/diagnostics`. Então a recusa dispara a consulta de
 * diagnósticos e a tela mostra a lista inteira, e não o primeiro erro: quem
 * escreveu um grafo com seis problemas corrige os seis de uma vez.
 */
export function FluxoPage({ definicaoId }: { definicaoId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeFluxo.definicoes,
    queryFn: listarDefinicoes,
  });

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando o fluxo" className="mx-auto max-w-4xl space-y-4">
        <SkeletonText lines={5} />
      </SkeletonRegion>
    );
  }

  if (consulta.isError) {
    return (
      <div className="mx-auto max-w-4xl">
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
      </div>
    );
  }

  const definicao = consulta.data.find((item) => item.id === definicaoId);

  if (!definicao) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <p className="text-sm text-muted-foreground">
          Este fluxo não existe, ou está fora do seu escopo.
        </p>
        <LinkDaListaDeFluxos className="text-sm text-primary underline-offset-4 hover:underline">
          Voltar para a lista de fluxos
        </LinkDaListaDeFluxos>
      </div>
    );
  }

  return <Conteudo definicao={definicao} />;
}

function Conteudo({ definicao }: { definicao: DefinicaoDeFluxo }) {
  const rascunho = definicao.versions.find((versao) => versao.status === 'DRAFT') ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="space-y-3">
        <LinkDaListaDeFluxos className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Fluxos
        </LinkDaListaDeFluxos>

        <div className="space-y-2">
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">{definicao.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Chave valor={definicao.key} />
            <span className="text-muted-foreground">aplica-se a</span>
            <Chave valor={definicao.entityType} />
          </div>
          {definicao.description ? (
            <p className="max-w-2xl text-sm text-muted-foreground">{definicao.description}</p>
          ) : null}
        </div>
      </header>

      <Card>
        <CardHeader
          title="Versões"
          description="Publicar aposenta a anterior. Aposentar não expulsa ninguém: um chamado na v1 continua percorrendo a v1 até fechar."
        />
        <CardBody className="p-0">
          {definicao.versions.length === 0 ? (
            <p className="px-5 py-6 text-sm text-muted-foreground">
              Nenhuma versão ainda. Salve um rascunho abaixo para criar a primeira.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {definicao.versions.map((versao) => (
                <LinhaDeVersao key={versao.id} versao={versao} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Rascunho"
          description="O rascunho é salvo sem validação nenhuma — um fluxo pela metade tem de poder ser guardado. A validação acontece na publicação."
        />
        <CardBody className="space-y-4">
          <EditorDeRascunho definicao={definicao} rascunho={rascunho} />
        </CardBody>
      </Card>
    </div>
  );
}

function LinhaDeVersao({ versao }: { versao: ResumoDeVersao }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm tabular-nums">v{versao.versionNumber}</span>
        <EtiquetaDeVersao status={versao.status} />
      </div>
      <span className="text-xs text-muted-foreground">
        {versao.publishedAt
          ? `publicada em ${formatarDataHora(versao.publishedAt)}`
          : versao.status === 'DRAFT'
            ? 'ainda não publicada'
            : 'sem data de publicação registrada'}
      </span>
    </li>
  );
}

// ── rascunho ───────────────────────────────────────────────────────────────

/**
 * O editor.
 *
 * ## O que o servidor não devolve, e como a tela lida com isso
 *
 * Não existe rota que **leia** o grafo de uma versão: a listagem traz quatro
 * campos e nenhum deles é o `graph`. Quem salva recebe o grafo de volta na
 * resposta — e é só isso que a tela pode mostrar depois. Então o campo começa
 * vazio quando a sessão não salvou nada, com um aviso dizendo exatamente isso.
 *
 * A alternativa seria o campo aparecer vazio sem explicação, e alguém
 * concluir que o rascunho tinha sumido — e salvar por cima do trabalho de
 * ontem achando que estava recomeçando do zero.
 */
function EditorDeRascunho({
  definicao,
  rascunho,
}: {
  definicao: DefinicaoDeFluxo;
  rascunho: ResumoDeVersao | null;
}) {
  const cliente = useQueryClient();
  const [texto, setTexto] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);
  const [diagnosticos, setDiagnosticos] = useState<Diagnostico[] | null>(null);
  const [publicada, setPublicada] = useState(false);
  /** O carimbo do rascunho, quando esta sessão o salvou. */
  const [carimbo, setCarimbo] = useState<number | null>(null);

  const analise = lerDefinicao(texto);

  const salvar = useMutation({
    mutationFn: () => {
      if (analise.graph === null) throw new Error('JSON inválido.');
      return salvarRascunho(definicao.id, analise.graph, carimbo ?? undefined);
    },
    onMutate: () => {
      setFalha(null);
      setDiagnosticos(null);
      setPublicada(false);
    },
    onSuccess: (versao) => {
      setCarimbo(versao.version);
      cliente.setQueryData(chavesDeFluxo.grafo(versao.id), versao.graph);
      void cliente.invalidateQueries({ queryKey: chavesDeFluxo.definicoes });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const validar = useMutation({
    mutationFn: (versaoId: string) => obterDiagnosticos(versaoId),
    onMutate: () => {
      setFalha(null);
      setPublicada(false);
    },
    onSuccess: (lista) => setDiagnosticos(lista),
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const publicar = useMutation({
    mutationFn: (versaoId: string) => publicarVersao(versaoId),
    onMutate: () => {
      setFalha(null);
      setDiagnosticos(null);
      setPublicada(false);
    },
    onSuccess: () => {
      setPublicada(true);
      setCarimbo(null);
      void cliente.invalidateQueries({ queryKey: chavesDeFluxo.definicoes });
    },
    onError: async (erro, versaoId) => {
      const classificada = classificarFalha(erro);
      setFalha(classificada);
      // A recusa da publicação traz a lista inteira **como texto**, porque o
      // filtro global do servidor só devolve `message`. A lista estruturada,
      // com o caminho de cada problema, vem daqui — e é ela que a tela mostra.
      if (classificada.tipo === 'recusa') {
        try {
          setDiagnosticos(await obterDiagnosticos(versaoId));
        } catch {
          // Sem os diagnósticos estruturados sobra a mensagem do 422, que já
          // enumera os problemas em texto. Perder o formato é melhor do que
          // perder a informação.
        }
      }
    },
  });

  const ocupado = salvar.isPending || validar.isPending || publicar.isPending;

  return (
    <div className="space-y-5">
      <Secao
        titulo="Definição em JSON"
        descricao={
          <>
            O grafo inteiro: <code className="font-mono text-xs">key</code>,{' '}
            <code className="font-mono text-xs">entityType</code>,{' '}
            <code className="font-mono text-xs">initialState</code>,{' '}
            <code className="font-mono text-xs">states</code> e{' '}
            <code className="font-mono text-xs">transitions</code>. A chave precisa ser a mesma da
            definição.
          </>
        }
        acao={
          <Button
            variant="secondary"
            size="sm"
            disabled={ocupado}
            onClick={() => setTexto(modeloMinimo(definicao))}
          >
            Modelo mínimo
          </Button>
        }
      >
        <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
          <span>
            A API não tem rota que leia o grafo de uma versão salva — a listagem devolve só número,
            situação e data. Por isso este campo começa vazio mesmo quando já existe rascunho
            gravado: <strong className="font-semibold">salvar substitui o rascunho inteiro</strong>,
            então cole a definição completa antes de gravar.
          </span>
        </p>

        <textarea
          aria-label="Definição do fluxo em JSON"
          spellCheck={false}
          rows={18}
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          className={`${CLASSE_DE_CAMPO} font-mono text-xs leading-relaxed`}
        />

        {texto.trim() !== '' && analise.erro ? (
          <p role="alert" className="text-xs text-destructive">
            JSON inválido: {analise.erro}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            loading={salvar.isPending}
            disabled={analise.graph === null || ocupado}
            onClick={() => salvar.mutate()}
          >
            Salvar rascunho
          </Button>

          {rascunho ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                loading={validar.isPending}
                disabled={ocupado}
                onClick={() => validar.mutate(rascunho.id)}
                icon={<FileWarning className="h-4 w-4" aria-hidden />}
              >
                Validar sem publicar
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={publicar.isPending}
                disabled={ocupado}
                onClick={() => publicar.mutate(rascunho.id)}
                icon={<Upload className="h-4 w-4" aria-hidden />}
              >
                Publicar v{rascunho.versionNumber}
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">
              Salve para criar a v{proximoNumeroDeVersao(definicao)} e liberar a publicação.
            </span>
          )}
        </div>

        {salvar.isSuccess && !salvar.isPending && falha === null ? (
          <p className="text-xs text-success">Rascunho salvo.</p>
        ) : null}
      </Secao>

      {falha ? <AvisoDeFalha falha={falha} /> : null}

      {publicada ? (
        <p className="flex items-center gap-2 rounded-md border border-success/25 bg-success/5 px-3 py-2 text-sm text-success">
          <CheckCircle2 className="h-4 w-4 flex-none" aria-hidden />
          Versão publicada. Os chamados que já seguiam a versão anterior continuam nela.
        </p>
      ) : null}

      {diagnosticos !== null ? (
        <Secao
          titulo={
            diagnosticos.length === 0
              ? 'Sem problemas'
              : `${diagnosticos.length} problema(s) impedem a publicação`
          }
          descricao={
            diagnosticos.length === 0
              ? 'A validação não encontrou nada. Este grafo publica.'
              : 'A API devolve todos de uma vez, com o caminho de cada um dentro da definição. Corrija todos antes de tentar de novo.'
          }
        >
          {diagnosticos.length > 0 ? <ListaDeDiagnosticos itens={diagnosticos} /> : null}
        </Secao>
      ) : null}
    </div>
  );
}

// ── apoio ──────────────────────────────────────────────────────────────────

/**
 * Um grafo válido e mínimo, com a chave da definição já preenchida.
 *
 * Dois estados e uma transição: é o menor grafo que passa pela validação —
 * todo estado alcançável, nenhum beco sem saída, terminal sem saída. Serve de
 * ponto de partida para quem nunca escreveu um.
 */
function modeloMinimo(definicao: DefinicaoDeFluxo): string {
  return `${JSON.stringify(
    {
      key: definicao.key,
      entityType: definicao.entityType,
      initialState: 'aberto',
      states: [
        { key: 'aberto', name: 'Aberto', category: 'NEW', slaClock: true },
        { key: 'fechado', name: 'Fechado', category: 'CLOSED', terminal: true },
      ],
      transitions: [
        {
          key: 'fechar',
          name: 'Fechar',
          from: ['aberto'],
          to: 'fechado',
          requireReason: true,
        },
      ],
    },
    null,
    2,
  )}\n`;
}
